import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import Accordion from '../../../../../components/Accordion';
import Card from '../../../../../components/Card';
import Invoices from '../../../../../components/Invoices';
import LinkButton from '../../../../../components/LinkButton';
import PaymentMethods from '../../../../../components/PaymentMethods';
import ENDPOINT from '../../../../../constants/endpoints';
import useApi from '../../../../../hooks/useApi';
import ConfirmDialog from '../../../../Dialog/components/ConfirmDialog';
import ResponseErrorDialog from '../../../../Dialog/components/ResponseErrorDialog'
import './SubscriptionSection.css'
import UpdateBillingDialog from '../../../../Dialog/components/UpdateBillingDialog';

const BillingSection = () => {
  const api = useApi();
  const [info, setInfo] = useState({
    loading: true,
    features: [],
    invoices: [],
    product: null,
    subscription: null,
    customer: null,
    paymentMethods: [],
  });

  const updateBillingInfo = useCallback(() => {
    UpdateBillingDialog({
      title: 'Update Billing Information',
      dismissWhenClose: true,
      textSuccessBtn: 'Update',
      textCancelBtn: 'Back',
      className: 'popup-recovered',
      data: info.customer,
      popupStyles: {
        maxWidth: 520,
      }
    }).then((customer) => {
      setInfo({...info, customer});
      toast.success('Billing information updated');
    })
  }, [info]);

  const billingActions = useMemo(() => {
    if (info.loading) {
      return [];
    }

    return [
      { label: 'Update Information', onClick: updateBillingInfo },
    ];
  }, [info]);

  const paymentMethodsActions = useMemo(() => {
    return [
      {
        label: 'Add Payment Method',
        onClick: () => {
          api.postRequest(
            ENDPOINT.ACTION.ACCOUNT.CREATE_PAYMENT_METHOD,
            {},
            {
              handleError: response => {
                ResponseErrorDialog({response});
                setInfo({...info, loading: false});
              },
              handleSuccess: response => {
                setInfo({...info, loading: false});
                window.location.href = response.url;
              },
            }
          );
        }
      },
    ];
  }, [info]);

  const getSubscriptionInfo = useCallback(() => {
    api.getRequest(
      ENDPOINT.FORM.GET.ACCOUNT_PLAN,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response => {
          setInfo({ ...info, ...response, loading: false });
        },
      }
    );
  }, []);

  const deletePaymentMethod = useCallback((id) => {
    ConfirmDialog({
      title: 'Delete Payment Method',
      bodyRender: (
        <p>Are you sure you want to delete this payment method?</p>
      ),
      textSuccessBtn: 'Delete',
      textCancelBtn: 'Cancel',
      className: 'popup-recovered',
    }).then(() => {
      api.postRequest(
        ENDPOINT.ACTION.ACCOUNT.DELETE_PAYMENT_METHOD,
        {id},
        {
          handleError: response => ResponseErrorDialog({response}),
          handleSuccess: response => {
            getSubscriptionInfo();
            toast.success('Payment method deleted');
          },
        }
      );
    });
  }, []);

  const setDefaultPaymentMethod = useCallback((id) => {
    ConfirmDialog({
      title: 'Set this payment method as default',
      bodyRender: (
        <p>Are you sure you want to set this payment method as default?</p>
      ),
      textSuccessBtn: 'Confirm',
      textCancelBtn: 'Cancel',
      className: 'popup-recovered',
    }).then(() => {
      api.postRequest(
        ENDPOINT.ACTION.ACCOUNT.SET_DEFAULT_PAYMENT_METHOD,
        {id},
        {
          handleError: response => ResponseErrorDialog({response}),
          handleSuccess: response => {
            getSubscriptionInfo();
            toast.success('Payment method set as default');
          },
        }
      );
    });
  }, []);

  useEffect(() => {
    getSubscriptionInfo();
  }, []);

  return (
    <>
      <div className={'form__content form__content--full-width'} style={{marginBottom: 16}}>
        <div className="form__header" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <h4 className="form__title">Billing Information</h4>
          <LinkButton link={'/candidate/account#subscription'} text={'See My Current Plan'} />
        </div>

        <div className={'form__group subscription-section'}>
          <Card title={'Billing information'} loading={info.loading} actions={billingActions}>
            {info.customer && (
              <div className={'billing-info'}>
                <div className={'billing-info__line'}>
                  <span className={'billing-info__label'}>Name</span>
                  <span className={'billing-info__content'}>{info.customer.name}</span>
                </div>
                <div className={'billing-info__line'}>
                  <span className={'billing-info__label'}>Email</span>
                  <span className={'billing-info__content'}>{info.customer.email}</span>
                </div>
              </div>
            )}
            {!info.customer && (
              <p>Billing information not added yet.</p>
            )}
          </Card>
          <Card title={'Payment Methods'} loading={info.loading} actions={paymentMethodsActions}>
            {info.paymentMethods.length === 0 && (
              <p>Payment method not added yet.</p>
            )}
            {info.paymentMethods.length > 0 && (
              <PaymentMethods
                data={info.paymentMethods}
                defaultId={info.customer.invoice_settings.default_payment_method}
                extended={true}
                canDelete={true}
                onDelete={deletePaymentMethod}
                onMakeDefault={setDefaultPaymentMethod}
              />
            )}
          </Card>
          <Accordion label={'Invoice history'} isLoading={info.loading}>
            {info.invoices.length > 0 ? (
              <Invoices data={info.invoices} />
            ) : (
              <p>Invoice history will be available after the first payment.</p>
            )}
            <p>For billing inquiries or additional information, please contact us at <LinkButton link={'mailto:support@gobuyside.com'} text={'support@gobuyside.com'} /></p>
          </Accordion>
        </div>
      </div>
    </>
  );
}

export default BillingSection;
